import React, { useState, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import { getDatabase, ref, push, serverTimestamp } from 'firebase/database';
import Footer from './Footer';
import LanguageSwitch from './LanguageSwitch';
import './PartnerCapture.css';

const PartnerCapture = () => {
  const { language } = useLanguage();
  const t = translations[language].partnerCapture;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    agency: '',
    website: '',
    clients: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const formRef = useRef(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.agency) return;

    setIsSubmitting(true);
    try {
      const db = getDatabase();
      const partnersRef = ref(db, 'partner-applications');
      await push(partnersRef, {
        ...formData,
        timestamp: serverTimestamp(),
        source: 'partner-capture',
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error('Partner capture error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pc-wrapper">
      {/* Minimal Nav */}
      <nav className="pc-nav">
        <div className="pc-nav-inner">
          <div className="pc-nav-logo-wrap">
            <img src="../img/bonusclub_logo.webp" alt="BonusClub" className="pc-nav-logo" />
            <span className="pc-nav-partner-tag">{t.navTag}</span>
          </div>
          <LanguageSwitch />
          <button className="pc-nav-cta" onClick={scrollToForm}>
            {t.navCta}
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="pc-hero">
        <div className="pc-hero-glow pc-hero-glow-1" />
        <div className="pc-hero-glow pc-hero-glow-2" />
        <div className="pc-hero-glow pc-hero-glow-3" />

        <div className="pc-hero-inner">
          {/* Left, Video + Copy */}
          <div className="pc-hero-content">
            <span className="pc-hero-badge">{t.badge}</span>
            <h1 className="pc-hero-title">{t.title}</h1>
            <p className="pc-hero-subtitle">{t.subtitle}</p>

            <div className="pc-video-wrap">
              <div className="pc-video-container">
                <div className="pc-video-placeholder">
                  <div className="pc-play-btn" onClick={() => {
                    const iframe = document.querySelector('.pc-video-iframe');
                    if (iframe) {
                      iframe.style.display = 'block';
                      iframe.src = iframe.dataset.src + '&autoplay=1';
                      document.querySelector('.pc-play-btn').style.display = 'none';
                      document.querySelector('.pc-video-overlay').style.display = 'none';
                    }
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                  <div className="pc-video-overlay" />
                  <iframe
                    className="pc-video-iframe"
                    data-src="https://www.youtube.com/embed/YOUR_PARTNER_VIDEO_ID?rel=0&modestbranding=1"
                    title="BonusClub Partner Program"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            </div>

            {/* Value Props */}
            <div className="pc-value-grid">
              {t.valueProps.map((prop, i) => (
                <div key={i} className="pc-value-item">
                  <div className="pc-value-icon">{prop.icon}</div>
                  <div className="pc-value-text">
                    <strong>{prop.title}</strong>
                    <span>{prop.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right, Application Form */}
          <div className="pc-hero-form-col" ref={formRef}>
            <div className="pc-form-card">
              <div className="pc-form-glow" />

              {!isSubmitted ? (
                <>
                  <div className="pc-form-header">
                    <span className="pc-form-eyebrow">{t.formEyebrow}</span>
                    <h2 className="pc-form-title">{t.formTitle}</h2>
                    <p className="pc-form-desc">{t.formDesc}</p>
                  </div>

                  <form onSubmit={handleSubmit} className="pc-form">
                    <div className="pc-input-row">
                      <div className="pc-input-group">
                        <label className="pc-label">{t.nameLabel}</label>
                        <input
                          type="text"
                          className="pc-input"
                          placeholder={t.namePlaceholder}
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="pc-input-group">
                        <label className="pc-label">{t.emailLabel}</label>
                        <input
                          type="email"
                          className="pc-input"
                          placeholder={t.emailPlaceholder}
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div className="pc-input-row">
                      <div className="pc-input-group">
                        <label className="pc-label">{t.agencyLabel}</label>
                        <input
                          type="text"
                          className="pc-input"
                          placeholder={t.agencyPlaceholder}
                          value={formData.agency}
                          onChange={(e) => setFormData({ ...formData, agency: e.target.value })}
                          required
                        />
                      </div>
                      <div className="pc-input-group">
                        <label className="pc-label">{t.websiteLabel}</label>
                        <input
                          type="url"
                          className="pc-input"
                          placeholder={t.websitePlaceholder}
                          value={formData.website}
                          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="pc-input-group">
                      <label className="pc-label">{t.clientsLabel}</label>
                      <select
                        className="pc-input pc-select"
                        value={formData.clients}
                        onChange={(e) => setFormData({ ...formData, clients: e.target.value })}
                        required
                      >
                        <option value="" disabled>{t.clientsPlaceholder}</option>
                        {t.clientsOptions.map((opt, i) => (
                          <option key={i} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>

                    <button
                      type="submit"
                      className="pc-submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <span className="pc-spinner" />
                      ) : (
                        <>
                          {t.submitBtn}
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                          </svg>
                        </>
                      )}
                    </button>

                    <p className="pc-form-note">{t.formNote}</p>
                  </form>

                  <div className="pc-trust-row">
                    {t.trustItems.map((item, i) => (
                      <div key={i} className="pc-trust-item">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3.5 8.5L6.5 11.5L12.5 4.5" />
                        </svg>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="pc-success">
                  <div className="pc-success-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </div>
                  <h3 className="pc-success-title">{t.successTitle}</h3>
                  <p className="pc-success-desc">{t.successDesc}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="pc-how">
        <div className="pc-how-inner">
          <h2 className="pc-how-title">{t.howTitle}</h2>
          <p className="pc-how-subtitle">{t.howSubtitle}</p>
          <div className="pc-how-steps">
            {t.howSteps.map((step, i) => (
              <div key={i} className="pc-how-step">
                <div className="pc-how-step-num">{String(i + 1).padStart(2, '0')}</div>
                <div className="pc-how-step-line" />
                <h3 className="pc-how-step-title">{step.title}</h3>
                <p className="pc-how-step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof Strip */}
      <section className="pc-proof">
        <div className="pc-proof-inner">
          {t.proofStats.map((stat, i) => (
            <div key={i} className="pc-proof-stat">
              <span className="pc-proof-number">{stat.number}</span>
              <span className="pc-proof-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="pc-faq">
        <div className="pc-faq-inner">
          <h2 className="pc-faq-title">{t.faqTitle}</h2>
          <div className="pc-faq-list">
            {t.faqs.map((faq, i) => (
              <div
                key={i}
                className={`pc-faq-item ${openFaq === i ? 'pc-faq-open' : ''}`}
              >
                <button
                  className="pc-faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span>{faq.q}</span>
                  <svg
                    className={`pc-faq-chevron ${openFaq === i ? 'pc-faq-chevron-open' : ''}`}
                    width="16" height="16" viewBox="0 0 16 16"
                    fill="none" stroke="currentColor" strokeWidth="1.5"
                    strokeLinecap="round" strokeLinejoin="round"
                  >
                    <path d="M4 6l4 4 4-4" />
                  </svg>
                </button>
                <div className="pc-faq-answer">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>

          <button className="pc-faq-cta" onClick={scrollToForm}>
            {t.faqCta}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 17L17 7M17 7H7M17 7v10" />
            </svg>
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PartnerCapture;
